import { Injectable, inject, signal } from '@angular/core';
import { EmpleadoRepository } from 'src/app/domain/service/empleado/empleado-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { PersonaRepository } from 'src/app/domain/service/persona/persona-repository';
import { Persona } from 'src/app/domain/model/persona/persona';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { EmpleadoRequest, EmpleadoAdminResponse } from 'src/app/domain/model/empleado/empleado';
import { Perfil } from 'src/app/domain/model/catalogo/perfil';
import { Solucion } from 'src/app/domain/model/catalogo/solucion';
import { Proyecto } from 'src/app/domain/model/catalogo/proyecto';
import { TipoAsesorReal } from 'src/app/domain/model/catalogo/tipo-asesor-real';
import { TipoFacturacion } from 'src/app/domain/model/catalogo/tipo-facturacion';
import { GrupoConocimiento } from 'src/app/domain/model/catalogo/grupo-conocimiento';
import { HttpPerfilRepository } from 'src/app/infrastructure/adapters/catalogo/http-perfil.repository';
import { HttpSolucionRepository } from 'src/app/infrastructure/adapters/catalogo/http-solucion.repository';
import { HttpProyectoRepository } from 'src/app/infrastructure/adapters/catalogo/http-proyecto.repository';
import { HttpTipoAsesorRealRepository } from 'src/app/infrastructure/adapters/catalogo/http-tipo-asesor-real.repository';
import { HttpTipoFacturacionRepository } from 'src/app/infrastructure/adapters/catalogo/http-tipo-facturacion.repository';
import { HttpGrupoConocimientoRepository } from 'src/app/infrastructure/adapters/catalogo/http-grupo-conocimiento.repository';
import { HttpDatosNominaRepository } from 'src/app/infrastructure/adapters/empleado/http-datos-nomina.repository';
import { TIPO_EMPLEADO, YES_VALUE, NO_VALUE } from 'src/app/common/constants-ejb';
import { isValidEmail, formatAsMoneyLegacy } from 'src/app/common/input-utils';
import { PersonaSeleccionada } from 'src/app/presentation/common/busquedas/buscador-persona-dialog.component';

/**
 * Legacy: `CrearEmpleadoViewImpl.changeTipoEmpleado()` — el Salario
 * precargado desde nómina (`consultarDatosNomina`) solo se MUESTRA
 * formateado si el Tipo de Empleado elegido no es uno de estos 3 (Directivo,
 * Directivo Técnico, Operaciones); si lo es, se muestra en blanco pero el
 * campo sigue igual de deshabilitado. Criterio DISTINTO del que usa
 * Modificar para el mismo propósito (`LISTA_CARGO_ADMIN`, por cargo de
 * nómina, resuelto en el backend) — no confundirlos. Duplicado deliberado de
 * `TIPOS_EMPLEADO_SIN_SALARIO` de `ModificarEmpleadoFacade` (mismo criterio
 * de no extraer a común hasta un tercer consumidor).
 */
const TIPOS_EMPLEADO_SIN_SALARIO_CREAR = ['DR', 'DT', 'OP'];

/**
 * Legacy: `Constants.ASESOR_PREVENCION` ("AP") — ver mismo comentario en
 * `ModificarEmpleadoFacade`. Duplicado deliberadamente (no extraído a un
 * archivo compartido en esta fase) para no acoplar Crear/Modificar a un
 * módulo común que no existía antes de esta HU — si se agrega un tercer
 * consumidor, extraer entonces.
 */
const TIPO_FACTURACION_PREVENCION = 'AP';

/** Legacy: `UtilidadesBuscadores.llenarListBoxTipoAsignacion()` — igual que `ModificarEmpleadoFacade`. */
const OPCIONES_TIPO_ASIGNACION: CatalogoOption[] = [
  { codigo: 'Administrativo/Proyectos', descripcion: 'Administrativo/Proyectos' },
  { codigo: 'Movilidad', descripcion: 'Movilidad' },
  { codigo: 'Riesgos', descripcion: 'Riesgos' },
  { codigo: 'N/A', descripcion: 'N/A' },
];

/**
 * Legacy: `CrearEmpleadoViewImpl.addChangeHandlerTipoAsesorReal()` (auditoría
 * de fidelidad 2026-09-11, fase 25) — al cambiar "Tipo Consultor"
 * (`cdtipoAsesorReal`, `Constants.ASES_*`), "Tipo de Facturación"
 * (`cdtipoAsesor`) se filtra y en la mayoría de los casos se autoselecciona,
 * quedando deshabilitado salvo para Prevención:
 * - Prevención (`P`) → opciones `['AP','AR']`, habilitado, el usuario elige.
 * - Integral (`I`) → solo `AI`, autoseleccionado, deshabilitado.
 * - Riesgos (`R`) → solo `AR`, autoseleccionado, deshabilitado.
 * - Higiene (`H`) → solo `AP`, autoseleccionado, deshabilitado.
 * - Línea de Atención (`L`) u otro/ninguno → sin filtro (todas las opciones),
 *   sin autoselección, deshabilitado — quirk real del legado (el dropdown
 *   queda visualmente con todas las opciones pero bloqueado): se replica tal
 *   cual, no es un caso que el legado resuelva de otra forma.
 *
 * Este bloque no existía en la primera versión de `CrearEmpleadoFacade` —
 * ambos dropdowns eran independientes. Códigos verificados contra
 * `Constants.java` (`ASES_PREVENCION="P"`, `ASES_INTEGRAL="I"`,
 * `ASES_RIESGOS="R"`, `ASES_HIGIENE="H"`) y `ConstantsEJB.TipoAsesorAsociacion`
 * (`ASESOR_INTEGRAL="AI"`, `ASESOR_PREVENCION="AP"`, `ASESOR_RIESGOS="AR"`).
 */
const TIPO_FACTURACION_POR_TIPO_ASESOR_REAL: Record<string, { codigos: string[]; editable: boolean; autoSeleccion?: string }> = {
  P: { codigos: ['AP', 'AR'], editable: true },
  I: { codigos: ['AI'], editable: false, autoSeleccion: 'AI' },
  R: { codigos: ['AR'], editable: false, autoSeleccion: 'AR' },
  H: { codigos: ['AP'], editable: false, autoSeleccion: 'AP' },
};

/**
 * Facade del formulario "Crear Empleado/Consultor" (HU1259115).
 *
 * <p>Legacy: `CrearEmpleadoViewImpl` — mismo formulario que Modificar salvo
 * las diferencias documentadas en HU1259115.md §"Diferencias Crear vs
 * Modificar": sin bloque de nómina/beneficios funcional (el `.ui.xml` sí
 * tiene "Información Adicional" con Salario/Nivel de Estudio/Cargo/Fechas de
 * contrato, pero `CrearEmpleadoViewImpl.getDTO()` nunca lee esos widgets —
 * son de solo-preview de `MPSC_DATOS_NOMINA`, no se envían al guardar, ver
 * auditoría de fidelidad fase 25), `dsusuarioRed` obligatorio, `cdperfil`
 * obligatorio solo si es asesor (corregido en fase 25 — no incondicional,
 * ver Javadoc de {@link validar}), sin `validarTipAseConvenio` (RN-05, un
 * empleado nuevo no puede tener facturación asociada), y con el buscador de
 * persona (`siBuscarPersona`) en vez de datos ya precargados.</p>
 *
 * <p>La validación de obligatoriedad replica
 * `EmpleadoUseCase.validarCamposObligatorios()` del backend (mismo criterio,
 * mismo conjunto de campos) para dar feedback inmediato en cliente, pero sin
 * duplicar mensajes por campo — el legado usa un único mensaje agregado
 * (`WidgetsNamesConstants.properties:666`, `requiredFieldsNoData` = "Por
 * favor ingrese correctamente los siguientes campos"), replicado aquí igual
 * en {@link validar}. El backend sigue siendo la fuente de verdad final: si
 * el cliente deja pasar algo, el backend lo rechaza igual (seguridad-girop
 * §2.1 — nunca confiar solo en validación de cliente).</p>
 */
@Injectable()
export class CrearEmpleadoFacade {
  private readonly empleadoRepository = inject(EmpleadoRepository);
  private readonly catalogoRepository = inject(CatalogoRepository);
  private readonly personaRepository = inject(PersonaRepository);
  private readonly perfilRepository = inject(HttpPerfilRepository);
  private readonly solucionRepository = inject(HttpSolucionRepository);
  private readonly proyectoRepository = inject(HttpProyectoRepository);
  private readonly tipoAsesorRealRepository = inject(HttpTipoAsesorRealRepository);
  private readonly tipoFacturacionRepository = inject(HttpTipoFacturacionRepository);
  private readonly grupoConocimientoRepository = inject(HttpGrupoConocimientoRepository);
  private readonly datosNominaRepository = inject(HttpDatosNominaRepository);

  readonly opcionesTipoAsignacion = OPCIONES_TIPO_ASIGNACION;

  // ==================== Estado de carga ====================

  readonly cargandoCatalogos = signal(true);
  readonly guardando = signal(false);
  readonly mensaje = signal('');
  readonly mensajeTipo = signal<'info' | 'warning' | 'error'>('info');

  // ==================== Persona seleccionada (buscador) ====================

  readonly personaSeleccionada = signal<PersonaSeleccionada | null>(null);
  readonly municipioResidenciaDisplay = signal<string>('');
  readonly municipioPrestacionDisplay = signal<string>('');

  /**
   * "Ver detalle Persona" (legacy: `saVerDetalle`/`activity.verDetallePersona()`,
   * auditoría de fidelidad fase 25 — SÍ existe en Crear, no solo en
   * Modificar como afirmaba HU1259115.md antes de esta fase; se hace
   * visible recién después de seleccionar una persona, igual que
   * `setDatosNomina()` → `saVerDetalle.setVisible(true)` en el legado).
   */
  readonly personaDetalle = signal<Persona | null>(null);
  readonly cargandoPersonaDetalle = signal(false);
  readonly tiposEmpresaPersona = signal<CatalogoOption[]>([]);
  readonly tamaniosEmpresaPersona = signal<CatalogoOption[]>([]);
  private catalogosEmpresaPersonaCargados = false;

  // ==================== Datos de Nómina / "Información Adicional" (auditoría fase 25/26) ====================
  // Legacy: CrearEmpleadoActivity.consultarNomina() (MPSC_DATOS_NOMINA, tabla
  // externa a TPSC_EMPLEADOS) — al seleccionar una persona se consulta su
  // último registro de nómina activo y se precarga "Información Adicional".
  // Corrección fase 26: los 5 campos visibles (Salario/Nivel de Estudio/
  // Cargo/Fecha Inicio/Fecha Fin) son SIEMPRE de solo lectura en el legado,
  // con o sin registro de nómina — `setCamposInactivos(false)` los
  // deshabilita una única vez en el constructor y nunca se revierte (grep
  // exhaustivo de `setEnabled(true)` sobre esos 5 widgets: cero resultados).
  // No hay estado de "habilitado/deshabilitado" que gestionar en el
  // frontend: el componente que los muestra es 100% solo lectura. Los
  // valores SÍ viajan en el POST (ya son parte de `EmpleadoRequest`/`form`),
  // igual que los 5 campos sin widget propio (dstipoContrato/dscodigoNomina/
  // dscentroCostos/dsjefeInmediato/dsfactorTiempo).

  readonly cargandoDatosNomina = signal(false);

  // ==================== Catálogos ====================

  readonly tiposEmpleado = signal<CatalogoOption[]>([]);
  readonly oficinas = signal<CatalogoOption[]>([]);
  readonly perfiles = signal<Perfil[]>([]);
  readonly tiposAsesorReal = signal<TipoAsesorReal[]>([]);
  readonly proyectos = signal<Proyecto[]>([]);
  readonly soluciones = signal<Solucion[]>([]);
  readonly tiposFacturacion = signal<TipoFacturacion[]>([]);
  readonly gruposConocimiento = signal<GrupoConocimiento[]>([]);

  // ==================== Formulario ====================

  form: EmpleadoRequest = this.formVacio();

  private formVacio(): EmpleadoRequest {
    return {
      cdpersona: 0,
      nmconsecOficina: 0,
      cdtipoEmpleado: '',
      dsusuarioRed: '',
    };
  }

  /** Ver Javadoc de `ModificarEmpleadoFacade.esAsesor()` sobre por qué no es un `computed()`. */
  esAsesor(): boolean {
    return this.form.cdtipoEmpleado === TIPO_EMPLEADO.ASESOR;
  }

  /** Legacy: `addChangeHandlerTipoAsesor()` — igual que Modificar. */
  mostrarProgramable(): boolean {
    return this.form.cdtipoAsesor !== TIPO_FACTURACION_PREVENCION;
  }

  async cargarCatalogos(): Promise<void> {
    this.cargandoCatalogos.set(true);
    try {
      const [tiposEmpleado, oficinas, perfiles, tiposAsesorReal, proyectos, soluciones, tiposFacturacion, gruposConocimiento] = await Promise.all([
        this.catalogoRepository.getTiposEmpleado(),
        this.catalogoRepository.getOficinas(),
        this.perfilRepository.listarActivos(),
        this.tipoAsesorRealRepository.listarActivos(),
        this.proyectoRepository.listarActivos(),
        this.solucionRepository.listarActivos(),
        this.tipoFacturacionRepository.listarActivos(),
        this.grupoConocimientoRepository.listarActivos(),
      ]);
      this.tiposEmpleado.set(tiposEmpleado);
      this.oficinas.set(oficinas);
      this.perfiles.set(perfiles);
      this.tiposAsesorReal.set(tiposAsesorReal);
      this.proyectos.set(proyectos);
      this.soluciones.set(soluciones);
      this.tiposFacturacion.set(tiposFacturacion);
      this.gruposConocimiento.set(gruposConocimiento);
    } catch (error) {
      console.error('Error cargando catálogos de Crear Empleado:', error);
    } finally {
      this.cargandoCatalogos.set(false);
    }
  }

  /** Reinicia el formulario a un estado vacío — usado al reabrir el modal para un nuevo registro. */
  reiniciar(): void {
    this.form = this.formVacio();
    this.personaSeleccionada.set(null);
    this.municipioResidenciaDisplay.set('');
    this.municipioPrestacionDisplay.set('');
    this.mensaje.set('');
    this.limpiarEstadoNomina();
  }

  private limpiarEstadoNomina(): void {
    this.form.ptsalarioBasico = undefined;
    this.form.dsnivelEstudio = undefined;
    this.form.dscargo = undefined;
    this.form.feinicioContrato = undefined;
    this.form.fefinContrato = undefined;
    this.form.dstipoContrato = undefined;
    this.form.dscodigoNomina = undefined;
    this.form.dscentroCostos = undefined;
    this.form.dsjefeInmediato = undefined;
    this.form.dsfactorTiempo = undefined;
  }

  /**
   * Legacy: `CrearEmpleadoActivity.consultarNomina()` → `CrearEmpleadoViewImpl.setDatosNomina()`.
   * Se dispara al seleccionar una persona (ver {@link seleccionarPersona}).
   * Los 5 campos visibles son siempre de solo lectura (ver Javadoc de la
   * sección "Datos de Nómina" arriba) — este método solo puebla `form`, sin
   * gestionar ningún estado de habilitado/deshabilitado.
   */
  private async cargarDatosNomina(cdpersona: number): Promise<void> {
    this.limpiarEstadoNomina();
    this.cargandoDatosNomina.set(true);
    try {
      const nomina = await this.datosNominaRepository.consultar(cdpersona);
      if (!nomina) {
        // Legacy: datosNomina == null -> los 5 campos quedan vacíos (igual de solo lectura).
        return;
      }
      this.form.ptsalarioBasico = nomina.ptsalarioBasico ?? undefined;
      this.form.dsnivelEstudio = nomina.dsnivelEstudio ?? undefined;
      this.form.dscargo = nomina.dscargo ?? undefined;
      this.form.feinicioContrato = nomina.feinicioContrato ?? undefined;
      this.form.fefinContrato = nomina.fefinContrato ?? undefined;
      // Sin widget propio en Crear — viajan igual al guardar (ver Javadoc de la sección arriba).
      this.form.dstipoContrato = nomina.dstipoContrato ?? undefined;
      this.form.dscodigoNomina = nomina.dscodigoNomina ?? undefined;
      this.form.dscentroCostos = nomina.dscentroCostos ?? undefined;
      this.form.dsjefeInmediato = nomina.dsjefeInmediato ?? undefined;
      this.form.dsfactorTiempo = nomina.dsfactorTiempo ?? undefined;
    } catch (error) {
      console.error('Error consultando datos de nómina:', error);
    } finally {
      this.cargandoDatosNomina.set(false);
    }
  }

  /**
   * Legacy: `CrearEmpleadoViewImpl.changeTipoEmpleado()` — texto de Salario
   * mostrado (formateado) solo si hay valor > 0 y el Tipo de Empleado elegido
   * no es Directivo/DirTecnico/Operaciones; en caso contrario, blanco. El
   * campo es siempre de solo lectura (ver Javadoc de la sección "Datos de
   * Nómina" arriba), esto solo gobierna qué texto se muestra.
   */
  salarioDisplay(): string {
    const salario = this.form.ptsalarioBasico;
    if (salario == null || salario <= 0) {
      return '';
    }
    if (TIPOS_EMPLEADO_SIN_SALARIO_CREAR.includes(this.form.cdtipoEmpleado)) {
      return '';
    }
    return formatAsMoneyLegacy(salario);
  }

  /**
   * Legacy: `siBuscarPersona` → `CrearEmpleadoActivity.postOpenDialog()`
   * (`crearEmpleadoView.reset()` + `consultarNomina()`) — precarga tipo/
   * número de identificación, nombre y correo (solo lectura), y **reinicia
   * el resto del formulario** cada vez que se selecciona una persona
   * (incluida una selección que reemplaza a una anterior), para no arrastrar
   * datos de oficina/tipo de empleado/condicionales de asesor de la persona
   * previamente elegida. Corregido en la auditoría de fidelidad (fase 25):
   * la primera versión solo seteaba `cdpersona`/correo sin resetear el resto.
   */
  seleccionarPersona(persona: PersonaSeleccionada): void {
    this.form = this.formVacio();
    this.personaSeleccionada.set(persona);
    this.personaDetalle.set(null);
    this.municipioResidenciaDisplay.set('');
    this.municipioPrestacionDisplay.set('');
    this.form.cdpersona = persona.cdpersona;
    if (persona.dsemail) {
      this.form.dsemailCorporativo = persona.dsemail;
    }
    void this.cargarDatosNomina(persona.cdpersona);
  }

  limpiarPersona(): void {
    this.personaSeleccionada.set(null);
    this.personaDetalle.set(null);
    this.form.cdpersona = 0;
    this.limpiarEstadoNomina();
  }

  /**
   * Legacy: `activity.verDetallePersona()` (`CrearEmpleadoActivity`) — abre
   * un diálogo de solo lectura con los datos completos de la Persona
   * seleccionada. A diferencia de Modificar (donde `cdpersona` sale del
   * `EmpleadoDetalle` ya cargado), aquí sale de `personaSeleccionada()`
   * (la persona recién elegida en el buscador, aún sin guardar como
   * empleado). Mismo patrón de cacheo de catálogos de tipo/tamaño de
   * empresa que `ModificarEmpleadoFacade.cargarDetallePersona()`.
   */
  async cargarDetallePersona(): Promise<void> {
    const cdpersona = this.personaSeleccionada()?.cdpersona;
    if (!cdpersona) {
      return;
    }
    this.cargandoPersonaDetalle.set(true);
    try {
      const [persona, tiposEmpresa, tamaniosEmpresa] = await Promise.all([
        this.personaRepository.getById(cdpersona),
        this.catalogosEmpresaPersonaCargados ? this.tiposEmpresaPersona() : this.catalogoRepository.getTiposEmpresa(),
        this.catalogosEmpresaPersonaCargados ? this.tamaniosEmpresaPersona() : this.catalogoRepository.getTamaniosEmpresa(),
      ]);
      this.personaDetalle.set(persona);
      this.tiposEmpresaPersona.set(tiposEmpresa);
      this.tamaniosEmpresaPersona.set(tamaniosEmpresa);
      this.catalogosEmpresaPersonaCargados = true;
    } catch (error) {
      console.error('Error cargando detalle de persona:', error);
      this.personaDetalle.set(null);
    } finally {
      this.cargandoPersonaDetalle.set(false);
    }
  }

  seleccionarMunicipioResidencia(codigo: string, display: string): void {
    this.form.cdmunResidencia = codigo;
    this.municipioResidenciaDisplay.set(display);
  }

  limpiarMunicipioResidencia(): void {
    this.form.cdmunResidencia = undefined;
    this.municipioResidenciaDisplay.set('');
  }

  seleccionarMunicipioPrestacion(codigo: string, display: string): void {
    this.form.cdmunPrestacion = codigo;
    this.municipioPrestacionDisplay.set(display);
  }

  limpiarMunicipioPrestacion(): void {
    this.form.cdmunPrestacion = undefined;
    this.municipioPrestacionDisplay.set('');
  }

  /**
   * Legacy: `changeTipoEmpleado()` — ver Javadoc de
   * `ModificarEmpleadoFacade.onCambioTipoEmpleado()` para la justificación de
   * los valores literales ('N'/''), replicados igual aquí.
   */
  onCambioTipoEmpleado(): void {
    if (this.esAsesor()) {
      this.sembrarDefaultsAsesor();
      return;
    }
    this.form.cdtipoAsesorReal = undefined;
    this.form.nmconsecutivoProyecto = undefined;
    this.form.nmconsecutivoSolucion = undefined;
    this.form.sndisponibleProgramacion = NO_VALUE;
    this.form.sncomputador = '';
    this.form.sntablet = '';
    this.form.snmodem = '';
    this.form.sncelular = undefined;
    this.form.dstipoAsignacion = '';
    this.form.nmcapacidad = undefined;
    this.form.sncalcProd = undefined;
    this.form.cdmunResidencia = undefined;
    this.form.cdmunPrestacion = undefined;
    this.form.snadscrito = NO_VALUE;
    this.form.ptvalorCobrarAdscrito = undefined;
    this.form.cdtipoAsesor = undefined;
    this.form.nmgrupoConocimiento = undefined;
    this.form.cdnumLicenciaSo = undefined;
    this.form.feresolucionLicenciaSo = undefined;
    this.form.dsentidadExpideLicSo = undefined;
    this.form.dstransporte = undefined;
    this.form.snruta = undefined;
    this.form.snmixto = undefined;
    this.form.snpymes = undefined;
    this.form.snespecializacionHigiene = NO_VALUE;
    this.form.snriesgosProgram = NO_VALUE;
    this.form.snatencionMasiva = NO_VALUE;
    this.form.snbloqueoAgenda = NO_VALUE;
    this.limpiarMunicipioResidencia();
    this.limpiarMunicipioPrestacion();
  }

  /** Legacy: `inicializarCamposVisibles(true)` — ver Javadoc de `ModificarEmpleadoFacade.sembrarDefaultsAsesor()`. */
  private sembrarDefaultsAsesor(): void {
    this.form.snadscrito ??= NO_VALUE;
    this.form.sndisponibleProgramacion ??= NO_VALUE;
    this.form.snlider ??= NO_VALUE;
    this.form.snruta ??= YES_VALUE;
    this.form.snmixto ??= YES_VALUE;
    this.form.snpymes ??= NO_VALUE;
    this.form.snatencionMasiva ??= NO_VALUE;
    this.form.snbloqueoAgenda ??= NO_VALUE;
    this.form.dstransporte ??= 'VARIABLE';
    this.form.snespecializacionHigiene ??= NO_VALUE;
  }

  /**
   * Legacy: `addChangeHandlerTipoAsesorReal()` (auditoría de fidelidad,
   * fase 25) — ver Javadoc de {@link TIPO_FACTURACION_POR_TIPO_ASESOR_REAL}.
   * Filtra/autoselecciona "Tipo de Facturación" según el "Tipo Consultor"
   * elegido; siempre limpia la selección anterior de facturación primero
   * (igual que el legado, que resetea `lbTipoFacturacion` a blanco en cada
   * cambio antes de recargar sus opciones).
   */
  onCambioTipoAsesorReal(): void {
    const regla = this.form.cdtipoAsesorReal
      ? TIPO_FACTURACION_POR_TIPO_ASESOR_REAL[this.form.cdtipoAsesorReal]
      : undefined;
    this.form.cdtipoAsesor = regla?.autoSeleccion;
    // onCambioTipoFacturacion() ya decide si hay que limpiar "Programable"
    // cuando el nuevo valor de facturación no es Prevención.
    this.onCambioTipoFacturacion();
  }

  /**
   * Opciones de "Tipo de Facturación" filtradas por el "Tipo Consultor"
   * actual — ver Javadoc de {@link TIPO_FACTURACION_POR_TIPO_ASESOR_REAL}.
   * Sin Tipo Consultor seleccionado, no se filtra (mismo estado inicial que
   * el legado antes del primer `onChange`).
   */
  tiposFacturacionFiltradas(): TipoFacturacion[] {
    const regla = this.form.cdtipoAsesorReal
      ? TIPO_FACTURACION_POR_TIPO_ASESOR_REAL[this.form.cdtipoAsesorReal]
      : undefined;
    if (!regla) {
      return this.tiposFacturacion();
    }
    return this.tiposFacturacion().filter((t) => regla.codigos.includes(t.cdtipo));
  }

  /** Legacy: `lbTipoFacturacion.setEnabled(...)` — editable únicamente para Tipo Consultor = Prevención. */
  tipoFacturacionEditable(): boolean {
    const regla = this.form.cdtipoAsesorReal
      ? TIPO_FACTURACION_POR_TIPO_ASESOR_REAL[this.form.cdtipoAsesorReal]
      : undefined;
    return regla?.editable ?? false;
  }

  /** Legacy: al ocultar "Programable" (Tipo de Facturación = Prevención) se limpia su valor. */
  onCambioTipoFacturacion(): void {
    if (!this.mostrarProgramable()) {
      this.form.snriesgosProgram = undefined;
    }
  }

  /** Legacy: `addChangeHandlerNoAdscrito()` limpia el valor hora al desmarcar. */
  onCambioAdscrito(): void {
    if (this.form.snadscrito !== YES_VALUE) {
      this.form.ptvalorCobrarAdscrito = undefined;
    }
  }

  /** Legacy: `addChangeHandlerSiEspecHigiene()` — ver Javadoc de `ModificarEmpleadoFacade.onCambioEspecializacionHigiene()`. */
  onCambioEspecializacionHigiene(): void {
    if (this.form.snespecializacionHigiene === YES_VALUE) {
      this.form.cdnumLicenciaHigiene = undefined;
      this.form.feresolucionLicenciaHigiene = undefined;
      this.form.dsentidadExpideLicHigiene = undefined;
    }
  }

  /**
   * Replica `EmpleadoUseCase.validarCamposObligatorios()` del backend
   * (mismo conjunto de campos, mismo criterio) para dar feedback inmediato
   * en cliente — sin mensajes por campo, un único mensaje agregado igual al
   * legado (`requiredFieldsNoData`). Devuelve `true` si el formulario está
   * completo.
   *
   * <p>Correcciones de la auditoría de fidelidad (fase 25):</p>
   * <ul>
   *   <li>"perfil" se movió dentro del bloque de asesor — el legado
   *   (`CrearEmpleadoViewImpl.setCamposRequeridos()`, línea 846:
   *   `this.lbPerfil.setRequired(true)`) solo lo exige cuando el tipo de
   *   empleado es Asesor/DirTecnico/AsesorLineaAt, nunca
   *   incondicionalmente — el backend (`EmpleadoUseCase`) ya lo tenía
   *   correcto (`isBlank(cdperfil)` dentro de `isAsesor()`); el error estaba
   *   solo en esta validación de cliente y en `HU1259115.md`, que afirmaba
   *   "perfil incondicionalmente obligatorio en Crear" sin haber leído el
   *   `.java` completo.</li>
   *   <li>Se agregó validación de formato de correo (`Utilidades.validateEmail()`
   *   en el legado, `CrearEmpleadoViewImpl.java:891`) — antes solo se
   *   sanitizaba el valor (XSS), sin verificar que fuera un email
   *   sintácticamente válido antes de guardar.</li>
   * </ul>
   */
  validar(): boolean {
    const errores: string[] = [];
    const f = this.form;

    if (!this.personaSeleccionada() || !f.cdpersona) {
      errores.push('persona');
    }
    if (!f.nmconsecOficina) {
      errores.push('oficina');
    }
    if (!f.cdtipoEmpleado?.trim()) {
      errores.push('tipo de empleado');
    }
    if (!f.dsusuarioRed?.trim()) {
      errores.push('usuario de red');
    }
    if (f.dsemailCorporativo?.trim() && !isValidEmail(f.dsemailCorporativo)) {
      errores.push('correo corporativo (formato inválido)');
    }

    if (this.esAsesor()) {
      if (!f.cdperfil?.trim()) errores.push('perfil');
      if (!f.cdtipoAsesorReal?.trim()) errores.push('tipo de asesor real');
      if (!f.sndisponibleProgramacion?.trim()) errores.push('disponible para programación');
      if (!f.sncomputador?.trim()) errores.push('computador');
      if (!f.sntablet?.trim()) errores.push('tablet');
      if (!f.snmodem?.trim()) errores.push('modem');
      if (!f.sncelular?.trim()) errores.push('celular');
      if (!f.dstipoAsignacion?.trim()) errores.push('tipo de asignación');
      if (f.nmcapacidad == null) errores.push('capacidad');
      if (!f.nmconsecutivoProyecto) errores.push('proyecto');
      if (!f.sncalcProd?.trim()) errores.push('calcula productividad');
      if (!f.cdmunResidencia?.trim()) errores.push('municipio de residencia');
      if (!f.cdmunPrestacion?.trim()) errores.push('municipio de prestación');
      if (!f.nmconsecutivoSolucion) errores.push('solución');
      if (!f.cdtipoAsesor?.trim()) errores.push('tipo de facturación');
      if (!f.dstransporte?.trim()) errores.push('transporte');
      if (!f.snruta?.trim()) errores.push('rutas');
      if (!f.snmixto?.trim()) errores.push('mixto');
      if (!f.snpymes?.trim()) errores.push('pymes');
    }

    if (f.snadscrito === YES_VALUE && f.ptvalorCobrarAdscrito == null) {
      errores.push('valor hora a cobrar (adscrito)');
    }

    if (f.snespecializacionHigiene === YES_VALUE) {
      if (!f.cdnumLicenciaHigiene?.trim()) errores.push('número de licencia de higiene');
      if (!f.feresolucionLicenciaHigiene) errores.push('fecha de resolución de licencia de higiene');
      if (!f.dsentidadExpideLicHigiene?.trim()) errores.push('entidad que expide licencia de higiene');
    }

    if (errores.length > 0) {
      // Legacy: `WidgetsNamesConstants.properties:666` (`requiredFieldsNoData`) — mensaje único agregado, no por campo.
      this.mensaje.set('Por favor ingrese correctamente los siguientes campos: ' + errores.join(', '));
      this.mensajeTipo.set('warning');
      return false;
    }
    return true;
  }

  async guardar(): Promise<EmpleadoAdminResponse | null> {
    if (!this.validar()) {
      return null;
    }
    this.guardando.set(true);
    this.mensaje.set('');
    try {
      return await this.empleadoRepository.crear(this.form);
    } catch (error: unknown) {
      console.error('Error creando empleado:', error);
      this.mensaje.set(this.extraerMensajeError(error));
      this.mensajeTipo.set('error');
      return null;
    } finally {
      this.guardando.set(false);
    }
  }

  private extraerMensajeError(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'error' in error) {
      const body = (error as { error?: { message?: string } }).error;
      if (body?.message) return body.message;
    }
    return 'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.';
  }
}
